'use client';

import { useEffect, useRef, useState } from 'react';
import { micromark } from 'micromark';
import DOMPurify from 'dompurify';
import type {
  DesignSession,
  WebsiteGuidance,
  WebsiteGuidanceInput,
} from '@open-design/contracts';
import { platform } from './api';
import { Button, Input, Select, Textarea } from './ui';
import css from './WebsitePlaybook.module.css';

const blank: WebsiteGuidanceInput = {
  slug: '',
  title: '',
  kind: 'pattern',
  summary: '',
  applicability: '',
  instructions: '',
  code_example: '',
  enabled: false,
};
const base = 'design/guidance/';
const safeAssetUrl = (path: string) =>
  /^\/api\/design\/guidance\/[a-zA-Z0-9_-]+\/attachments\/[a-f0-9-]+\/$/.test(
    path,
  )
    ? '/platform' + path
    : '';

export function WebsitePlaybook({
  session,
  onBack,
}: {
  session: DesignSession;
  onBack: () => void;
}) {
  const [entries, setEntries] = useState<WebsiteGuidance[]>([]);
  const [selected, setSelected] = useState<WebsiteGuidance | null>(null);
  const [draft, setDraft] = useState<WebsiteGuidanceInput | null>(null);
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!session.guidance?.can_view) return;
    const controller = new AbortController();
    void platform<WebsiteGuidance[]>(base, { signal: controller.signal })
      .then(setEntries)
      .catch((e) => {
        if (!controller.signal.aborted) setError(e.message);
      })
      .finally(() => setLoading(false));
    const warn = (event: BeforeUnloadEvent) => {
      if (dirtyRef.current) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      controller.abort();
      window.removeEventListener('beforeunload', warn);
    };
  }, [session.guidance?.can_view]);
  function leave() {
    return !dirty || window.confirm('Discard your unsaved playbook changes?');
  }
  function choose(entry: WebsiteGuidance | null, edit = false) {
    if (!leave()) return;
    setSelected(entry);
    setDraft(
      edit
        ? entry
          ? { ...entry, expected_updated_at: entry.updated_at }
          : { ...blank }
        : null,
    );
    setDirty(false);
    setError('');
    setNotice('');
  }
  function update(key: keyof WebsiteGuidanceInput, value: string | boolean) {
    setDraft((current) => (current ? { ...current, [key]: value } : current));
    setDirty(true);
  }
  function accept(entry: WebsiteGuidance) {
    setEntries((current) =>
      [...current.filter((item) => item.slug !== entry.slug), entry].sort(
        (a, b) => a.title.localeCompare(b.title),
      ),
    );
    setSelected(entry);
    setDraft(null);
    setDirty(false);
    setNotice('Saved. Future requests will use the current enabled guidance.');
  }
  async function save() {
    if (!draft || busy) return;
    setBusy(true);
    setError('');
    try {
      const {
        slug,
        title,
        kind,
        summary,
        applicability,
        instructions,
        code_example,
        enabled,
      } = draft;
      accept(
        await platform<WebsiteGuidance>(
          base + (selected ? selected.slug + '/' : ''),
          {
            method: selected ? 'PATCH' : 'POST',
            csrf: session.csrf_token,
            body: {
              slug,
              title,
              kind,
              summary,
              applicability,
              instructions,
              code_example,
              enabled,
              ...(selected
                ? { expected_updated_at: draft.expected_updated_at }
                : {}),
            },
          },
        ),
      );
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : 'The entry could not be saved. Your draft is retained.',
      );
    } finally {
      setBusy(false);
    }
  }
  async function exportPlaybook() {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const bundle = await platform('design/guidance/export/');
      const url = URL.createObjectURL(
        new Blob([JSON.stringify(bundle, null, 2)], {
          type: 'application/json',
        }),
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = 'website-playbook.json';
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setNotice(
        'Exported enabled guidance. Use od website-guidance export --out to download the reference files too.',
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export failed.');
    } finally {
      setBusy(false);
    }
  }
  async function attach(file: File) {
    if (!selected || busy) return;
    if (file.size > 10 * 1024 * 1024) {
      setError('Attachments must be under 10 MiB.');
      return;
    }
    setBusy(true);
    setError('');
    const body = new FormData();
    body.append('file', file);
    body.append('expected_updated_at', selected.updated_at);
    try {
      const response = await fetch(
        `/platform/api/${base}${selected.slug}/attachments/`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'X-Design-CSRF': session.csrf_token },
          body,
        },
      );
      if (!response.ok) {
        const value = (await response.json().catch(() => null)) as
          | { detail?: string }
          | string
          | null;
        throw new Error(
          typeof value === 'string'
            ? value
            : (value?.detail ??
              'Upload failed. Refresh the entry if it was changed elsewhere.'),
        );
      }
      accept((await response.json()) as WebsiteGuidance);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed.');
    } finally {
      setBusy(false);
    }
  }
  async function remove(id: string) {
    if (!selected || busy) return;
    setBusy(true);
    setError('');
    try {
      accept(
        await platform<WebsiteGuidance>(
          `${base}${selected.slug}/attachments/${id}/`,
          {
            method: 'DELETE',
            csrf: session.csrf_token,
            body: { expected_updated_at: selected.updated_at },
          },
        ),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not remove reference.');
    } finally {
      setBusy(false);
    }
  }
  if (!session.guidance?.can_view)
    return (
      <main className={css.page}>
        <h1>Website playbook</h1>
        <p role="alert">This library is restricted to platform maintainers.</p>
        <Button onClick={onBack}>Back to websites</Button>
      </main>
    );
  return (
    <main className={css.page}>
      <header className={css.header}>
        <div>
          <p className={css.eyebrow}>Intelliminds Design</p>
          <h1>Website playbook</h1>
          <p>Strong starting points, adapted to each business.</p>
        </div>
        <div className={css.actions}>
          <Button disabled={busy} onClick={() => void exportPlaybook()}>
            Export guidance JSON
          </Button>
          <Button
            disabled={busy}
            onClick={() => {
              if (leave()) onBack();
            }}
          >
            Back to websites
          </Button>
        </div>
      </header>
      {error && (
        <p className={css.error} role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className={css.notice} role="status">
          {notice}
        </p>
      )}
      <div className={css.layout}>
        <aside className={css.sidebar}>
          <label className={css.field}>
            Search playbook
            <Input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>
          {session.guidance.can_manage && (
            <Button disabled={busy} onClick={() => choose(null, true)}>
              New entry
            </Button>
          )}
          {loading && <p role="status">Loading playbook…</p>}
          {!loading && entries.length === 0 && (
            <p>No guidance yet. Add the first entry to get started.</p>
          )}
          {entries
            .filter((item) =>
              `${item.title} ${item.summary}`
                .toLowerCase()
                .includes(query.toLowerCase()),
            )
            .map((item) => (
              <Button
                key={item.slug}
                className={css.listItem}
                aria-pressed={selected?.slug === item.slug}
                disabled={busy}
                onClick={() => choose(item)}
              >
                <strong>{item.title}</strong>
                <span>
                  {item.kind} · {item.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </Button>
            ))}
        </aside>
        <section className={css.detail} aria-label="Playbook entry">
          {draft ? (
            <form
              className={css.form}
              onSubmit={(e) => {
                e.preventDefault();
                void save();
              }}
            >
              <h2>{selected ? 'Edit guidance' : 'New guidance'}</h2>
              <label className={css.field}>
                Identifier
                <Input
                  required
                  pattern="[a-zA-Z0-9_-]+"
                  maxLength={100}
                  value={draft.slug}
                  disabled={Boolean(selected) || busy}
                  onChange={(e) => update('slug', e.target.value)}
                />
              </label>
              <label className={css.field}>
                Title
                <Input
                  required
                  maxLength={160}
                  value={draft.title}
                  disabled={busy}
                  onChange={(e) => update('title', e.target.value)}
                />
              </label>
              <label className={css.field}>
                Type
                <Select
                  value={draft.kind}
                  disabled={busy}
                  onChange={(e) => update('kind', e.target.value)}
                >
                  <option value="principle">Principle</option>
                  <option value="pattern">Pattern</option>
                </Select>
              </label>
              <label className={css.field}>
                Summary
                <Input
                  required
                  maxLength={500}
                  value={draft.summary}
                  disabled={busy}
                  onChange={(e) => update('summary', e.target.value)}
                />
              </label>
              <label className={css.field}>
                When to use or avoid
                <Textarea
                  maxLength={5000}
                  value={draft.applicability}
                  disabled={busy}
                  onChange={(e) => update('applicability', e.target.value)}
                />
              </label>
              <label className={css.field}>
                Instructions (Markdown)
                <Textarea
                  required
                  maxLength={30000}
                  value={draft.instructions}
                  disabled={busy}
                  onChange={(e) => update('instructions', e.target.value)}
                />
              </label>
              <label className={css.field}>
                Code example (displayed as text)
                <Textarea
                  maxLength={50000}
                  value={draft.code_example}
                  disabled={busy}
                  onChange={(e) => update('code_example', e.target.value)}
                />
              </label>
              <label className={css.check}>
                <Input
                  type="checkbox"
                  checked={draft.enabled}
                  disabled={busy}
                  onChange={(e) => update('enabled', e.target.checked)}
                />
                Enable for future agent requests
              </label>
              <div className={css.actions}>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={busy || (Boolean(selected) && !dirty)}
                >
                  {busy ? 'Saving…' : 'Save guidance'}
                </Button>
                <Button
                  disabled={busy}
                  onClick={() => {
                    if (leave()) {
                      setDraft(null);
                      setDirty(false);
                    }
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          ) : selected ? (
            <>
              <div className={css.header}>
                <div>
                  <p className={css.eyebrow}>
                    {selected.kind} ·{' '}
                    {selected.enabled ? 'Enabled' : 'Disabled'}
                  </p>
                  <h2>{selected.title}</h2>
                </div>
                {session.guidance.can_manage && (
                  <Button
                    disabled={busy}
                    onClick={() => choose(selected, true)}
                  >
                    Edit guidance
                  </Button>
                )}
              </div>
              <p>{selected.summary}</p>
              <h3>When to use</h3>
              <p className={css.prewrap}>{selected.applicability}</p>
              <div
                className={css.markdown}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(micromark(selected.instructions)),
                }}
              />
              {selected.code_example && (
                <>
                  <h3>Code reference</h3>
                  <pre className={css.code}>
                    <code>{selected.code_example}</code>
                  </pre>
                </>
              )}
              <h3>Reference files</h3>
              <p>
                References are inspected, never executed. Upload JPG, PNG, WebP,
                Markdown or plain text (10 MiB maximum).
              </p>
              {selected.attachments.map((asset) => (
                <article className={css.asset} key={asset.id}>
                  {asset.content_type.startsWith('image/') && (
                    <img
                      src={safeAssetUrl(asset.url)}
                      alt={asset.filename}
                      loading="lazy"
                    />
                  )}
                  <a
                    href={safeAssetUrl(asset.url)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {asset.filename}
                  </a>
                  {session.guidance?.can_manage && (
                    <Button
                      disabled={busy}
                      onClick={() => void remove(asset.id)}
                    >
                      Remove reference
                    </Button>
                  )}
                </article>
              ))}
              {session.guidance.can_manage && (
                <label className={css.field}>
                  Add reference
                  <Input
                    type="file"
                    disabled={busy}
                    accept=".jpg,.jpeg,.png,.webp,.txt,.md"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) void attach(file);
                      e.target.value = '';
                    }}
                  />
                </label>
              )}
            </>
          ) : (
            <div className={css.empty}>
              <h2>Our preferred starting points.</h2>
              <p>
                Select a principle or pattern to inspect the guidance and
                examples. Existing sites keep their own approved identity.
              </p>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
